import { Button, Col, Divider, List, notification, Row, Table, UploadFile } from '@pankod/refine-antd';
import { useNavigation } from '@pankod/refine-core';
import { UploadChangeParam } from 'antd/lib/upload';
import { DownloadCsvTemplateCard, ImportCsvDataCard } from 'components';
import { useState } from 'react';
import { importExpenses } from 'services/expense';
import {
  EXPENSE_CSV_HEADER,
  expenseCsvRecordToImportRow,
  IExpenseImportRow,
} from 'utilities/expense-csv';
import { readCSV } from 'utilities/file';

const templateRows = [
  EXPENSE_CSV_HEADER,
  ['', '05/10/2026', 'Rent', '', '', 'Expense', 'Cash', '', '15000', 'October rent'],
  ['', '31/10/2026', 'Utilities', '', '', 'Expense', 'Card', '', '1250', ''],
];

const previewColumns = [
  { title: 'Expense Date', dataIndex: 'expenseDate', key: 'expenseDate' },
  { title: 'Expense Category', dataIndex: 'category', key: 'category' },
  { title: 'Payment Type', dataIndex: 'paymentType', key: 'paymentType' },
  { title: 'Payment Method', dataIndex: 'paymentMethod', key: 'paymentMethod' },
  { title: 'Amount', dataIndex: 'amount', key: 'amount' },
  { title: 'Note', dataIndex: 'note', key: 'note' },
];

export const ImportExpenses: React.FC = () => {
  const [parsedRows, setParsedRows] = useState<IExpenseImportRow[]>([]);
  const [isButtonLoading, setIsButtonLoading] = useState<boolean>(false);

  const { list } = useNavigation();

  const onFileUploadChange = async (info: UploadChangeParam<UploadFile<any>>) => {
    try {
      if (!info.fileList.length) {
        return;
      }
      const file = info.fileList[0].originFileObj!;
      const csvRecords: Record<string, string>[] = await readCSV({ file });
      setParsedRows(csvRecords.map(expenseCsvRecordToImportRow));
    } catch (err) {
      setParsedRows([]);
      if (err instanceof Error) {
        notification.error({ message: 'Failed', description: err.message });
      }
    }
  };

  const onFileRemove = (_file: UploadFile<any>) => {
    setParsedRows([]);
  };

  const onSubmitButtonClick = async () => {
    setIsButtonLoading(true);
    try {
      const importedCount = await importExpenses(parsedRows);
      notification.success({
        message: 'Success',
        description: `Imported ${importedCount} expenses`,
      });
      list('expenses');
    } catch (err: any) {
      const apiMessage = err?.response?.errors?.[0]?.message;
      notification.error({
        message: 'Nothing was imported',
        description: (
          <span style={{ whiteSpace: 'pre-line' }}>
            {apiMessage ?? (err instanceof Error ? err.message : 'Import failed')}
          </span>
        ),
      });
    }
    setIsButtonLoading(false);
  };

  return (
    <List title="Import Expenses">
      <Row gutter={[48, 16]} wrap style={{ marginBottom: '2rem' }}>
        <Col xs={11} lg={8}>
          <DownloadCsvTemplateCard rows={templateRows} title="Download .csv template for import" />
        </Col>
        <Col xs={11} sm={11} lg={8}>
          <ImportCsvDataCard onFileUploadChange={onFileUploadChange} onFileRemove={onFileRemove} />
        </Col>
      </Row>
      <p>
        Upload the file from Export on the expense list, or the template. Each row becomes a new
        expense. Warehouse, Client/Supplier and Transaction Type are not imported. If any row has a
        problem, nothing is saved.
      </p>

      <Divider />
      <Row wrap={false}>
        <Col flex="auto">
          <Table
            columns={previewColumns}
            dataSource={parsedRows.map((row, index) => ({ ...row, key: index }))}
          />
        </Col>
      </Row>
      <Row justify="center" align="middle" style={{ marginTop: '1rem' }}>
        <Button
          style={{ width: '300px' }}
          loading={isButtonLoading}
          disabled={!parsedRows.length}
          onClick={onSubmitButtonClick}
        >
          Save
        </Button>
      </Row>
    </List>
  );
};
